import { Button, Card, Col, Modal, Row, Select, Skeleton, Input, Segmented, Space } from 'antd';
import { faArrowDownLong, faArrowUpLong } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState, useEffect, ReactNode } from 'react';
import { projectTypeOptionsFor, withoutUnavailableProjectTypes } from '../../../common/constants';
import useServiceTicketsEnabled from '../../../hooks/useServiceTicketsEnabled';
import VSpace from '../../common/VSpace';
import { DatacenterReportTypes, DateRanges, getDatacenterReportTypeNames, getFiltersFromJson, ReportFilterViewModel, updateFilterDates } from '../datacenterModel';
import { useCreateFiltersMutation, useDeleteFiltersMutation, useGetFiltersQuery } from '../datacenterQueries';
import ReportFilters from '../ReportFilters';
import ReportSavedFiltersList from '../ReportSavedFiltersList';
import ReportStatistics from '../ReportStatistics';
import SaveFilterModal from '../SaveFilterModal';
import EstimateReportFilterDetails from './EstimateReportFilterDetails';
import { EstimatesSummary } from './estimatesReportApi';
import EstimatesReportJobList from './EstimatesReportJobList';
import EstimatesKanbanBoard from './EstimatesKanbanBoard';
import { getFilteredEstimatesData, getEstimateStatistics, getUniqueDivisionNames, getUniqueSalesPersonNames } from './estimatesReportModel';
import { defaultEstimatesBoardSort, EstimatesBoardSort } from './estimatesKanbanModel';
import { boardSortColumn, boardSortOptions } from '../../JobList/jobListFiltersModel';
import { useEstimatesReportDataQuery } from './estimatesReportQueries';
import { useSavedState } from '../../../hooks/useSavedState';
import { useDivisions } from '../../../domain/divisions/divisionsQueries';
import './EstimatesReport.css';
import ControlField from '../../common/ControlField';
import ExportCsvButton from '../ExportCsvButton';
import { defaultEstimatesTableSort, estimateExportColumns, getEstimateExportRows } from './estimatesReportExport';
import { sortForExport } from '../reportCsvExport';
import useDashboardReporting from '../../../hooks/useDashboardReporting';

export type EstimatesReportFilters = {
  startDate: Dayjs;
  endDate: Dayjs;
  salesPersonIds: string[];
  projectTypeIds: number[];
  dateRange: DateRanges;
  divisionIds: string[];
  searchTerm: string;
  archived: boolean;
}

type EstimatesReportProps = {
  summaryMode?: boolean;
  starting?: string
  ending?: string
  viewToggle?: ReactNode
  densityToggle?: ReactNode
  showKanban?: boolean
  condensed?: boolean
}

const EstimatesReport = ({ summaryMode = false, starting, ending, viewToggle, densityToggle, showKanban, condensed }: EstimatesReportProps) => {
  const showServiceTickets = useServiceTicketsEnabled()
  const dashboardReporting = useDashboardReporting()
  const projectTypeOptions = projectTypeOptionsFor(showServiceTickets)
  const today = dayjs();
  const defaultStartDate = starting ? dayjs(starting) : today.subtract(6, 'month');
  const defaultEndDate = ending ? dayjs(ending) : today;
  const defaultFilters: EstimatesReportFilters = {
    startDate: defaultStartDate,
    endDate: defaultEndDate,
    salesPersonIds: [],
    projectTypeIds: [],
    dateRange: 'custom',
    divisionIds: [],
    searchTerm: '',
    archived: false,
  }
  const [open, setOpen] = useState(false);
  const [deleteFilterModalConfirm, deleteFiltersModalContext] = Modal.useModal();
  const [filterName, setFilterName] = useState('');
  const sortOptions = boardSortOptions({ sold: false, deleted: false });
  const [sort, setSort] = useSavedState<EstimatesBoardSort>('estimates-report-board-sort', defaultEstimatesBoardSort);
  const sortColumn = boardSortColumn(sort.column, sortOptions) ?? sortOptions[0].value;
  const [savedFilters, setFilters] = useSavedState<EstimatesReportFilters>('estimates-report-filters', defaultFilters, (serialized: any) => ({
    ...serialized,
    startDate: dayjs(serialized.startDate),
    endDate: dayjs(serialized.endDate),
    archived: serialized.archived ?? false,
  }));
  const filters = useMemo(() => ({
    ...savedFilters,
    projectTypeIds: withoutUnavailableProjectTypes(savedFilters.projectTypeIds, showServiceTickets),
  }), [savedFilters, showServiceTickets]);
  const { startDate, endDate, salesPersonIds, projectTypeIds, divisionIds, archived } = filters;

  useEffect(() => {
    if (starting || ending) {
      setFilters({
        ...filters,
        startDate: starting ? dayjs(starting) : filters.startDate,
        endDate: ending ? dayjs(ending) : filters.endDate,
      });
    }
  }, [starting, ending]);

  const estimatesReportQuery = useEstimatesReportDataQuery(
    startDate.toISOString().split('T')[0],
    endDate.toISOString().split('T')[0]
  );
  const getFiltersQuery = useGetFiltersQuery(DatacenterReportTypes.Estimates)
  const createFiltersMutation = useCreateFiltersMutation()
  const deleteFiltersMutation = useDeleteFiltersMutation()

  const estimatesData: EstimatesSummary = estimatesReportQuery.data?.groupedEstimatesData || {
    unsent: [],
    sent: [],
    approved: [],
    lost: []
  };
  const filtersList: ReportFilterViewModel[] = getFiltersQuery.data?.map((x) => {
    return {
      ...x,
      filters: getFiltersFromJson(x.filters)
    }
  }).sort((a, b) => a.order - b.order) || [];

  const filteredEstimatesData = useMemo<EstimatesSummary>(
    () => getFilteredEstimatesData(estimatesData, salesPersonIds, projectTypeIds, divisionIds, filters.searchTerm, archived),
    [estimatesData, salesPersonIds, projectTypeIds, divisionIds, filters.searchTerm, archived]
  );

  const salesPersonNames = useMemo<string[]>(
    () => estimatesData ? getUniqueSalesPersonNames(estimatesData) : [],
    [estimatesData]
  );

  const divisions = useDivisions()
  
  const divisionOptions = useMemo<string[]>(
    () => divisions.data ? getUniqueDivisionNames(divisions.data) : [],
    [divisions.data]
  );

  const onSaveFilter = () => {
    createFiltersMutation.mutate({
      request: {
        reportId: DatacenterReportTypes.Estimates,
        name: filterName,
        reportTypeName: getDatacenterReportTypeNames(DatacenterReportTypes.Estimates),
        filters: updateFilterDates(filters)
      },
      reportType: DatacenterReportTypes.Estimates
    });
    setOpen(false);
    setFilterName('');
  }

  if (estimatesReportQuery.isLoading) {
    return <Skeleton active />;
  }

  return (<Row gutter={16}>
    {!summaryMode && (filtersList.length > 0) && <Col span={4}>
        <ReportSavedFiltersList
          filtersList={filtersList}
          onFilterSelect={(filters) => setFilters(filters as EstimatesReportFilters)}
          onDeleteFilters={(selectedFiltersIds) => {
            deleteFilterModalConfirm.confirm({
              title: 'Do you want to delete the selected filters?',
              content: 'This action cannot be undone',
              onOk: () => {
                deleteFiltersMutation.mutate({ filtersIds: selectedFiltersIds, reportType: DatacenterReportTypes.Estimates })
              },
              okText: 'Delete',
              okButtonProps: {
                loading: deleteFiltersMutation.isLoading,
                danger: true
              }
            })
          }}
          renderFilterDetails={(item) => <EstimateReportFilterDetails filterItem={item} />}
          reportType={DatacenterReportTypes.Estimates }
        />
      </Col>
    }
    <Col span={(filtersList.length > 0) ? 20 : 24}>
      <VSpace>
        {!summaryMode && <Space align="end" wrap className="estimates-controls kanban-filter-row">
          <ControlField label="Search">
            <Input.Search
              placeholder="Filter by estimate name"
              value={filters.searchTerm}
              onChange={e => setFilters({ ...filters, searchTerm: e.target.value })}
              style={{ width: 360 }}
            />
          </ControlField>
          <ControlField label="Visibility">
            <Segmented
              options={[
                { label: 'Active', value: false },
                { label: 'Archived', value: true }
              ]}
              value={archived}
              onChange={(value) => setFilters({ ...filters, archived: value })}
            />
          </ControlField>
          {viewToggle && <ControlField label="View">{viewToggle}</ControlField>}
          {showKanban && densityToggle && <ControlField label="Cards">{densityToggle}</ControlField>}
          {showKanban && <ControlField label="Sort">
            <Space.Compact>
              <Select
                value={sortColumn}
                onChange={column => setSort({ ...sort, column })}
                options={sortOptions}
                style={{ width: 160 }}
              />
              <Button
                icon={<FontAwesomeIcon icon={sort.descending ? faArrowDownLong : faArrowUpLong} />}
                aria-label={sort.descending ? 'Sorted descending' : 'Sorted ascending'}
                onClick={() => setSort({ ...sort, descending: !sort.descending })}
              />
            </Space.Compact>
          </ControlField>}
          {dashboardReporting && <ExportCsvButton
            reportName='Estimates'
            dateRange={{ start: startDate, end: endDate }}
            columns={estimateExportColumns(divisions.data ?? [])}
            rows={sortForExport(getEstimateExportRows(filteredEstimatesData), defaultEstimatesTableSort)}
            tooltip='Exports every status, with a Status column'
          />}
        </Space>}
        <ReportFilters
          filters={filters}
          onFiltersChange={setFilters}
          isDataLoading={estimatesReportQuery.isLoading}
          defaultFilters={defaultFilters}
          onSaveFilters={() => {
            setOpen(true)
          }}
          isSummaryMode={summaryMode}
          selectOptions={{
            salesPersonIds: {
              mode: 'multiple',
              options: salesPersonNames.map((name) => ({
                label: name,
                value: name,
              })),
              placeholder: 'Select Sales Persons',
              style: { width: 300 },
            },
            projectTypeIds: {
              mode: 'multiple',
              options: projectTypeOptions,
              placeholder: 'Select Project Types',
              style: { width: 300 },
            },
            divisionIds: {
              mode: 'multiple',
              options: divisionOptions.map((name) => ({
                label: name,
                value: divisions.data?.find(x => x.name === name)?.id,
              })),
              placeholder: 'Select Divisions',
              style: { width: 300 },
            },
          }}
        />
        <ReportStatistics
          isSummaryMode={summaryMode} 
          title={
            <span style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>Summary</span>
            </span>
          }
          columnWidth={4}
          statistics={getEstimateStatistics(filteredEstimatesData)}
        />
        {!summaryMode && (showKanban
          ? <Card>
              <EstimatesKanbanBoard
                estimates={filteredEstimatesData}
                condensed={condensed}
                sortColumn={sortColumn}
                sortDescending={sort.descending}
              />
            </Card>
          : <EstimatesReportJobList
              estimates={filteredEstimatesData}
              divisions={divisions.data ?? []}
            />)}
      </VSpace>
    </Col>

    <SaveFilterModal
      inputValue={filterName}
      onCancel={() => {
        setFilterName('');
        setOpen(false);
      }}
      onInputChange={setFilterName}
      onOk={onSaveFilter}
      open={open}
      title='Save the Filter'
      okText='Save Filter'
      cancelText='Cancel'
    />
    {deleteFiltersModalContext}
  </Row>
  );
};

export default EstimatesReport;
